import { useEffect, useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { adminApi } from "@/lib/adminApi";
import { useConfirm } from "@/lib/useConfirm";
import ConfirmDialog from "@/components/ConfirmDialog";

// Admin manager for camp photos. Uploads to Cloudinary via the existing
// /admin/upload endpoint, then registers the URL against a camp via
// /admin/camp-photos. Photos surface in the parent portal gallery.
export default function CampPhotoManager() {
  const [camps, setCamps] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [filterCamp, setFilterCamp] = useState("all");
  const [uploadCamp, setUploadCamp] = useState("");
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { confirmState, requestConfirm, setOpen: setConfirmOpen } = useConfirm();

  const fetchAll = async () => {
    try {
      const [c, p] = await Promise.all([
        adminApi.get("/admin/camps"),
        adminApi.get("/admin/camp-photos"),
      ]);
      setCamps(c.data);
      setPhotos(p.data);
    } catch (e) {
      toast.error("Failed to load photos");
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!uploadCamp) {
      toast.error("Pick a camp first, then upload");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const upRes = await adminApi.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = upRes.data.url;
      await adminApi.post("/admin/camp-photos", { camp_id: uploadCamp, url, caption });
      setCaption("");
      toast.success("Photo uploaded");
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (photo) => {
    requestConfirm({
      title: "Delete this photo?",
      description: "Parents will no longer see it in their portal gallery. Note: the underlying image stays on Cloudinary; this just removes the database record.",
      confirmLabel: "Delete Photo",
      onConfirm: async () => {
        try {
          await adminApi.delete(`/admin/camp-photos/${photo.id}`);
          toast.success("Photo deleted");
          fetchAll();
        } catch (e) {
          toast.error("Failed to delete photo");
        }
      },
    });
  };

  const filtered = filterCamp === "all" ? photos : photos.filter((p) => p.camp_id === filterCamp);
  const campNameById = Object.fromEntries(camps.map((c) => [c.id, c.name]));

  return (
    <>
      <h2 className="font-heading text-2xl font-bold text-white mb-2">CAMP PHOTOS</h2>
      <p className="text-zinc-500 text-sm mb-6">Photos shown in the parent portal gallery for parents whose kids attended this camp.</p>

      {/* Upload row */}
      <div className="bg-zinc-950 border border-white/10 rounded-sm p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-zinc-400">Camp</Label>
            <Select value={uploadCamp} onValueChange={setUploadCamp}>
              <SelectTrigger className="bg-transparent border-white/20 text-white" data-testid="photo-upload-camp">
                <SelectValue placeholder="Pick a camp" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/20 text-white">
                {camps.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-400">Caption (optional)</Label>
            <Input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="e.g. Day 2 — pitching drills"
              className="bg-transparent border-white/20 text-white"
            />
          </div>
          <div className="flex items-end">
            <input ref={fileRef} type="file" accept="image/*" onChange={onPick} className="hidden" />
            <Button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || !uploadCamp}
              className="btn-primary w-full"
              data-testid="upload-photo-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload Photo
            </Button>
          </div>
        </div>
      </div>

      {/* Filter + grid */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-burns-gold text-sm">{filtered.length} photo{filtered.length === 1 ? "" : "s"}</span>
        <Select value={filterCamp} onValueChange={setFilterCamp}>
          <SelectTrigger className="bg-transparent border-white/20 text-white w-56" data-testid="photo-filter-camp">
            <SelectValue placeholder="Filter by camp" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-white/20 text-white">
            <SelectItem value="all">All camps</SelectItem>
            {camps.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-zinc-950 border border-white/10 rounded-sm">
          <ImageIcon className="w-16 h-16 text-burns-gold mx-auto mb-4" />
          <p className="text-zinc-400">No photos yet. Pick a camp above and upload.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filtered.map((p) => (
            <div key={p.id} className="bg-zinc-950 border border-white/10 rounded-sm overflow-hidden group relative">
              <img src={p.url} alt={p.caption || "Camp photo"} className="w-full aspect-square object-cover" />
              <div className="p-2">
                <p className="text-zinc-400 text-xs truncate">{campNameById[p.camp_id] || "—"}</p>
                {p.caption && <p className="text-zinc-500 text-xs truncate">{p.caption}</p>}
              </div>
              <button
                onClick={() => handleDelete(p)}
                className="absolute top-1 right-1 bg-black/60 hover:bg-burns-red/80 text-white p-1.5 rounded opacity-0 group-hover:opacity-100 transition"
                data-testid={`delete-photo-${p.id}`}
                aria-label="Delete photo"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmState.open}
        onOpenChange={setConfirmOpen}
        title={confirmState.title}
        description={confirmState.description}
        confirmLabel={confirmState.confirmLabel}
        cancelLabel={confirmState.cancelLabel}
        destructive={confirmState.destructive}
        onConfirm={confirmState.onConfirm}
      />
    </>
  );
}
